import { useStudies } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import { WithApolloContext } from 'modules/Apollo/types';
import { useMemo } from 'react';

/**
 * Groups prior studies by hl7Order so that studies from the same exam
 * are placed together in the `cases` query variable for GET_HANGING_PROTOCOL.
 *
 * Example: Given a CT and MR from one prior exam (same hl7Order) and an US from another:
 *   [['main-study'], ['ct-study', 'mr-study'], ['us-study']]
 */
export function useExamGroupedPriorStudies({ context }: WithApolloContext): string[][] {
  const { studies, mainStudiesIds, priorStudiesIds } = useStudies({ context });

  return useMemo(() => {
    if (mainStudiesIds.length === 0) return [];

    const priorIdsSet = new Set(priorStudiesIds);
    const examGroups = new Map<string, string[]>();

    studies.forEach((study) => {
      if (!priorIdsSet.has(study.smid)) return;

      if (!examGroups.has(study.hl7Order)) {
        examGroups.set(study.hl7Order, [study.smid]);
      } else {
        examGroups.get(study.hl7Order)!.push(study.smid);
      }
    });

    return [[...mainStudiesIds], ...examGroups.values()];
  }, [mainStudiesIds, studies, priorStudiesIds]);
}

/**
 * Treats each prior study as its own case group (no exam grouping).
 * Used as the `cases` query variable for GET_HANGING_PROTOCOL when
 * EXAM_GROUPED_HANGING_PROTOCOLS is OFF.
 *
 * Example: Given a CT and MR from the same prior exam and an US from another:
 *   [['main-study'], ['ct-study'], ['mr-study'], ['us-study']]
 */
export function usePriorStudyGroups({ context }: WithApolloContext): string[][] {
  const { mainStudiesIds, priorStudiesIds } = useStudies({ context });

  return useMemo(() => {
    if (mainStudiesIds.length === 0) return [];

    return [[...mainStudiesIds], ...priorStudiesIds.map((id) => [id])];
  }, [mainStudiesIds, priorStudiesIds]);
}
